import { paths } from "@/config/paths";
import {
  UserRole,
  INTERNAL_ROLES,
  type UserRole as UserRoleType,
} from "@/types/user";
import type { NavLink } from "./nav-item";
import { ROLE_PERMISSIONS, RESOURCES } from "@/shared/permissions-matrix";
import type { Resource } from "@/types/permissions";
import {
  LayoutDashboard,
  Building2,
  FileText,
  Target,
  ShoppingCart,
  Users,
  Briefcase,
  UserCheck,
  ScrollText,
  Settings,
  Receipt,
  Bell,
  BarChart3,
  ArrowRightLeft,
  Truck,
} from "lucide-react";

function getRolesForResource(resource: Resource): UserRoleType[] {
  return Object.keys(ROLE_PERMISSIONS).filter((role) => {
    const perms = ROLE_PERMISSIONS[role as UserRoleType];
    return perms.some((p) => p.resource === resource && p.actions.length > 0);
  }) as UserRoleType[];
}

function getRolesForResources(resources: Resource[]): UserRoleType[] {
  const roleSet = new Set<UserRoleType>();
  for (const resource of resources) {
    getRolesForResource(resource).forEach((r) => roleSet.add(r));
  }
  return Array.from(roleSet);
}

// ─── Role display labels ──────────────────────────────────────────────────────
export const roleLabels: Record<UserRoleType, string> = {
  [UserRole.SUPERADMIN]: "Superadmin",
  [UserRole.PM]: "Project Manager",
  [UserRole.SE]: "Site Engineer",
  [UserRole.ENGINEER]: "Engineer",
  [UserRole.FINANCE]: "Finance",
  [UserRole.CLIENT_VIEWER]: "Client",
  [UserRole.CLIENT_DECISION_MAKER]: "Client",
};

// ─── All nav items with role access control ────────────────────────────────────
// badge values are hardcoded for now — replace with live data once available
type NavItemConfig = {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  roles?: UserRoleType[];
  resource?: Resource | Resource[];
  badge?: number;
  sectionLabel?: string;
  category?: "general" | "admin" | "finance";
};

// Unified nav items array with categories
const navItems: NavItemConfig[] = [
  // ─── General items ─────────────────────────────────────────────────────────
  {
    label: "Dashboard",
    href: paths.app.dashboard.getHref(),
    icon: LayoutDashboard,
    roles: [UserRole.SUPERADMIN, UserRole.PM, UserRole.SE, UserRole.FINANCE],
    category: "general",
  },
  {
    label: "Projects",
    href: paths.app.projects.getHref(),
    icon: Building2,
    resource: RESOURCES.PROJECTS,
    badge: 12,
    category: "general",
  },
  {
    label: "Payable Invoices",
    href: paths.app.invoices.getHref(),
    icon: FileText,
    resource: [RESOURCES.PAYABLE_INVOICES, RESOURCES.CLIENT_INVOICES],
    badge: 4,
    category: "general",
  },
  {
    label: "Client Invoices",
    href: paths.app.clientInvoices.getHref(),
    icon: Receipt,
    resource: RESOURCES.CLIENT_INVOICES,
    category: "finance",
  },
  {
    label: "Payment Reports",
    href: paths.app.paymentReports.getHref(),
    icon: ScrollText,
    resource: RESOURCES.PAYMENT_REPORTS,
    category: "finance",
  },
  {
    label: "Milestones",
    href: paths.app.milestones.getHref(),
    icon: Target,
    resource: RESOURCES.MILESTONES,
    category: "general",
  },
  {
    label: "Change orders",
    href: paths.app.changeOrders.getHref(),
    icon: ArrowRightLeft,
    resource: RESOURCES.CHANGE_ORDERS,
    category: "general",
  },
  {
    label: "Purchase orders",
    href: paths.app.purchaseOrders.getHref(),
    icon: ShoppingCart,
    resource: RESOURCES.PURCHASE_ORDERS,
    category: "general",
  },
  {
    label: "Suppliers",
    href: paths.app.suppliers.getHref(),
    icon: Truck,
    resource: RESOURCES.SUPPLIERS,
    category: "general",
  },
  {
    label: "Reports",
    href: paths.app.reports.getHref(),
    icon: BarChart3,
    roles: [UserRole.SUPERADMIN, UserRole.PM, UserRole.FINANCE],
    category: "general",
  },

  // ─── Admin items ───────────────────────────────────────────────────────────
  {
    label: "Users",
    href: paths.app.users.getHref(),
    icon: Users,
    resource: RESOURCES.USERS,
    category: "admin",
  },
  {
    label: "Contractors",
    href: paths.app.contractors.getHref(),
    icon: Briefcase,
    resource: RESOURCES.CONTRACTORS,
    category: "admin",
  },
  {
    label: "Clients",
    href: paths.app.clients.getHref(),
    icon: UserCheck,
    resource: RESOURCES.CLIENTS,
    category: "admin",
  },
  {
    label: "Audit logs",
    href: paths.app.auditLogs.getHref(),
    icon: ScrollText,
    resource: RESOURCES.AUDIT_LOGS,
    category: "admin",
  },

  // ─── System items ─────────────────────────────────────────────────────────
  {
    label: "Notifications",
    href: paths.app.notifications.getHref(),
    icon: Bell,
    resource: RESOURCES.NOTIFICATIONS,
    category: "general",
  },
  {
    label: "Settings",
    href: paths.app.settings.getHref(),
    icon: Settings,
    roles: [UserRole.SUPERADMIN, UserRole.PM, UserRole.SE, UserRole.FINANCE],
    category: "general",
  },
];

// ─── Public API ────────────────────────────────────────────────────────────────

/**
 * Returns the sidebar navigation links filtered for the given role.
 * Follows PRD §26 — each role sees only its permitted items.
 */
export function getSideNavLinks(
  role?: UserRoleType | null,
  pendingMilestoneCount?: number,
  unbilledChangeOrderCount?: number
): NavLink[] {
  if (!role || !INTERNAL_ROLES.includes(role)) return [];

  // Filter nav items by role, deriving roles from permission matrix when resource is defined
  return navItems
    .filter((item) => {
      const itemRoles = item.resource
        ? getRolesForResources(
            Array.isArray(item.resource) ? item.resource : [item.resource]
          )
        : item.roles;
      return itemRoles?.includes(role);
    })
    .map(({ roles: _, category: __, ...rest }) => {
      if (
        rest.label === "Milestones" &&
        pendingMilestoneCount !== undefined &&
        pendingMilestoneCount > 0
      ) {
        return { ...rest, badge: pendingMilestoneCount };
      }
      if (
        rest.label === "Change orders" &&
        unbilledChangeOrderCount !== undefined &&
        unbilledChangeOrderCount > 0
      ) {
        return { ...rest, badge: unbilledChangeOrderCount };
      }
      return rest;
    });
}

/**
 * Returns the 3–4 most important nav items for the mobile bottom tab bar.
 * The remaining items are accessible via the "More" drawer.
 */
export function getBottomTabLinks(role?: UserRoleType | null): NavLink[] {
  if (!role || !INTERNAL_ROLES.includes(role)) return [];

  if (role === UserRole.FINANCE) {
    return [
      {
        label: "Dashboard",
        href: paths.app.dashboard.getHref(),
        icon: LayoutDashboard,
      },
      {
        label: "Payable Invoices",
        href: paths.app.invoices.getHref(),
        icon: FileText,
      },
      {
        label: "Client Invoices",
        href: paths.app.clientInvoices.getHref(),
        icon: Receipt,
      },
      { label: "Reports", href: paths.app.reports.getHref(), icon: BarChart3 },
    ];
  }

  const base: NavLink[] = [
    {
      label: "Dashboard",
      href: paths.app.dashboard.getHref(),
      icon: LayoutDashboard,
    },
    { label: "Projects", href: paths.app.projects.getHref(), icon: Building2 },
  ];

  // Add a 4th tab for roles that have enough items
  if (role === UserRole.SUPERADMIN || role === UserRole.PM) {
    base.push({
      label: "Invoices",
      href: paths.app.invoices.getHref(),
      icon: FileText,
    });
  }

  return base;
}

/**
 * Returns the remaining nav items not shown in the bottom tab bar,
 * used inside the "More" bottom-sheet drawer on mobile.
 */
export function getMoreLinks(
  role?: UserRoleType | null,
  pendingMilestoneCount?: number,
  unbilledChangeOrderCount?: number
): NavLink[] {
  if (!role || !INTERNAL_ROLES.includes(role)) return [];

  const bottomHrefs = new Set(getBottomTabLinks(role).map((l) => l.href));
  const allLinks = getSideNavLinks(role, pendingMilestoneCount, unbilledChangeOrderCount);
  return allLinks.filter((l) => !bottomHrefs.has(l.href));
}

/** Legacy export for backward compat — prefer getSideNavLinks(role) */
export const topNavLinks: NavLink[] = [
  {
    label: "Profile",
    href: paths.app.settings.profile.getHref(),
    icon: Settings,
  },
];

/** Legacy export — prefer getSideNavLinks(role) */
export const sideNavLinks: NavLink[] = [];
